<template>
  <div>
    <h2>{{ title }}</h2>

    <p>{{ t('landingpage/modpack/description') }}</p>

    <landing-page-modview-search-input
      class="w-100 mt-4"
      :value="searchTerm"
      @change="onSearchTermChange"
    />

    <landing-page-modview-grid-wrapper
      ref="modsGrid"
      class="mt-4"
      :items="items"
      :items-per-page="itemsPerPage"
      :lazy-loading-offset="2"
      :is-resetting="isResetting"
      @invoke-pagination="handlePagination"
      @select-mod="onModSelected"
    />

    <landing-page-modview-info-modal
      :item="selectedMod"
      @modal-closed="handleModalClose"
    />
  </div>
</template>

<script setup lang="ts">
import {
  GetMinecraftModpacksDocument,
  type GetMinecraftModpacksQuery,
  type GetMinecraftModpacksQueryVariables,
  type StrapiMinecraftModpack,
} from '~/apollo/types/types';
import { INITIAL_BATCH_SIZE } from './grid/Wrapper.vue';
import type { LandingPageModviewGridWrapper } from '#build/components';

const { md, smaller } = useAppBreakpoints();

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
});

const isSmallerMd = smaller('md');
// initialize with 9 items when in SSR
const itemsPerPage = ref<number>(9);
const searchTerm = ref<string>('');
const offset = ref<number>(0);
const batchSize = ref<number>(INITIAL_BATCH_SIZE);
const isResetting = ref<boolean>(false);

const { t, locale } = useI18n();
const { $sentry } = useNuxtApp();
const currencyStore = useCurrencyStore();
const fetch = async () => {
  try {
    // if we use pagination, we do not need a loading state
    isResetting.value = offset.value === 0;
    const variables: GetMinecraftModpacksQueryVariables = {
      region: 'eur',
      currency: currencyStore.currency,
      locale: locale.value,
      limit: batchSize.value,
      offset: offset.value,
    };

    if (searchTerm.value.length > 0) {
      variables.search = searchTerm.value;
    }

    const { data } = await useLegacyApollo<GetMinecraftModpacksQuery>(
      GetMinecraftModpacksDocument,
      variables,
    );

    isResetting.value = false;
    return data.modpacks.entries as Array<StrapiMinecraftModpack>;
  } catch (error) {
    $sentry.captureException(error);
  }

  return [] as Array<StrapiMinecraftModpack>;
};
const { refresh, data: items } = await useAsyncData<
  Array<StrapiMinecraftModpack>
>('gp-lp-mc-modpack', fetch);

const modsGrid = ref<InstanceType<typeof LandingPageModviewGridWrapper>>(null);
const resetFetchParams = async () => {
  modsGrid.value?.resetPosition();
  await nextTick();
  offset.value = 0;
  batchSize.value = INITIAL_BATCH_SIZE;
};

watch(
  () => (md.value && !isSmallerMd.value) || (!md.value && isSmallerMd.value),
  async (newValue, oldValue) => {
    if (oldValue) {
      // Upon initializing, both values are false because of SSR.
      // The first watch is triggered by vue itself on client side which changes either value to true, because we are in a browser environment
      // That means this first watch is wrong
      // On the other hand we want only to invoke the fetch,
      // if either of the values was true before which only occurs if you for example change the window size.
      itemsPerPage.value = md.value ? 9 : 8;
      await resetFetchParams();
      refresh();
    }
  },
);

watch(
  () => currencyStore.currency,
  async () => {
    await resetFetchParams();
    refresh();
  },
);

const onSearchTermChange = async (value) => {
  searchTerm.value = value;
  resetFetchParams();
  items.value = await fetch();
};

const handlePagination = async ({ batchSize: eventBatchSize }) => {
  offset.value = items.value.length;
  batchSize.value = eventBatchSize;

  items.value = items.value.concat(await fetch());
};

// modal features
const selectedMod = ref<StrapiMinecraftModpack>(null);
const onModSelected = (item) => {
  selectedMod.value = item;
};

const handleModalClose = () => {
  selectedMod.value = null;
};

onMounted(() => {
  // when initializing on client, we want to repair
  // the pagination for mobile to render 8 items per page
  // but css grid renders two row grid so the user does not notice
  // that sth changes when initializing because the end of the first page is in overflow
  itemsPerPage.value = md.value ? 9 : 8;
});
</script>
